/**
 * **Der Zeitplan eines Workflows: Lesen und Prüfen der fünf Felder** — Spec
 * `docs/superpowers/specs/2026-11-04-workflows-design.md`, `D-6`.
 *
 * Fünf Felder, durch Leerraum getrennt: Minute (0–59), Stunde (0–23), Tag des
 * Monats (1–31), Monat (1–12), Wochentag (0–7, `0` und `7` sind Sonntag). Je Feld
 * eine Liste (`1,15`) aus `*`, `*` mit Schritt, `a`, `a-b` und `a-b` mit Schritt.
 * **Keine Namen** (`MON`, `JAN`) und keine Sonderformen (`@daily`, `L`, `W`, `#`)
 * — was hier nicht steht, ist ein Befund und kein stilles Ignorieren.
 *
 * **Warum das Lesen im Vertrag steht und das Rechnen im Server.** Die Oberfläche
 * soll einen Ausdruck schon beim Tippen ablehnen können, und das Einstellungsschema
 * des Auslösers (`zeitplanAusdruckSchema` in `workflows.ts`) prüft ihn mit
 * genau dieser Funktion — dieselbe Prüfung an beiden Enden. Die nächste Ausführung
 * rechnet nur der Server (`apps/api/src/workflows/zeitplan.ts`), auf Grundlage von
 * {@link zerlegeZeitplan}.
 *
 * **Tag und Wochentag zugleich.** Wie bei cron: Sind beide Felder eingeschränkt
 * (beginnen nicht mit `*`), genügt EINES von beiden (`0 9 1 * 1` läuft am Ersten
 * UND an jedem Montag); beginnt eines mit `*` (auch mit Schritt), müssen beide
 * passen.
 *
 * **Mindesttakt 5 Minuten.** Gemessen wird nicht die Schreibweise, sondern der
 * kleinste Abstand zweier aufeinanderfolgender Minuten, die der Ausdruck trifft —
 * innerhalb einer Stunde und über die Stundengrenze, wenn zwei benachbarte Stunden
 * erlaubt sind. Damit fallen `*`, ein Schritt unter fünf und `0,1,2` gleichermassen.
 */
export const ZEITPLAN_FELDER = ['minute', 'stunde', 'tag', 'monat', 'wochentag'];
/** Der kleinste erlaubte Abstand zweier Ausführungen in Minuten (`D-6`). */
export const ZEITPLAN_MINDESTTAKT_MINUTEN = 5;
const GRENZEN = {
    minute: [0, 59],
    stunde: [0, 23],
    tag: [1, 31],
    monat: [1, 12],
    wochentag: [0, 7],
};
const FELDNAME = {
    minute: 'Minute',
    stunde: 'Stunde',
    tag: 'Tag',
    monat: 'Monat',
    wochentag: 'Wochentag',
};
/** Längster Monat je Monatsnummer (Februar mit Schaltjahr). */
const MONATSLAENGE = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
function ganzzahl(text) {
    return /^\d{1,2}$/.test(text) ? Number.parseInt(text, 10) : null;
}
/** Liest ein Feld; liefert die Werte oder einen Satz, was daran falsch ist. */
function liesFeld(feld, text) {
    const [min, max] = GRENZEN[feld];
    const werte = new Set();
    for (const teil of text.split(',')) {
        const [bereich = '', schrittText, ueberzaehlig] = teil.split('/');
        if (ueberzaehlig !== undefined || bereich === '')
            return `„${teil}“ ist keine gültige Angabe`;
        let schritt = 1;
        if (schrittText !== undefined) {
            const s = ganzzahl(schrittText);
            if (s === null || s < 1)
                return `„${teil}“ hat keinen gültigen Schritt`;
            schritt = s;
        }
        let von;
        let bis;
        if (bereich === '*') {
            von = min;
            bis = max;
        }
        else {
            const [a = '', b, zuviel] = bereich.split('-');
            const va = ganzzahl(a);
            const vb = b === undefined ? va : ganzzahl(b);
            if (zuviel !== undefined || va === null || vb === null) {
                return `„${teil}“ ist keine Zahl und kein Bereich (Namen wie MON oder JAN sind nicht erlaubt)`;
            }
            if (schrittText !== undefined && b === undefined) {
                return `„${teil}“: Ein Schritt braucht „*“ oder einen Bereich davor`;
            }
            von = va;
            bis = vb;
        }
        if (von < min || bis > max) {
            return `„${teil}“ liegt ausserhalb von ${String(min)} bis ${String(max)}`;
        }
        if (von > bis)
            return `„${teil}“: Der Bereich beginnt hinter seinem Ende`;
        for (let w = von; w <= bis; w += schritt)
            werte.add(feld === 'wochentag' && w === 7 ? 0 : w);
    }
    return [...werte].sort((x, y) => x - y);
}
/** Der kleinste Abstand zweier getroffener Minuten, über die Stundengrenze, wenn sie erreichbar ist. */
function kleinsterAbstand(minuten, stunden) {
    let kleinster = Number.POSITIVE_INFINITY;
    for (let i = 1; i < minuten.length; i += 1) {
        kleinster = Math.min(kleinster, (minuten[i] ?? 0) - (minuten[i - 1] ?? 0));
    }
    const benachbart = stunden.some((h) => stunden.includes((h + 1) % 24));
    const erste = minuten[0];
    const letzte = minuten[minuten.length - 1];
    if (benachbart && erste !== undefined && letzte !== undefined) {
        kleinster = Math.min(kleinster, 60 - letzte + erste);
    }
    return kleinster;
}
/**
 * Liest einen Ausdruck in seine fünf Wertemengen — oder liefert den ersten Befund.
 *
 * Prüft Form und Wertebereiche, **nicht** den Mindesttakt und nicht, ob der Tag
 * je eintritt; das tut {@link pruefeZeitplan}.
 */
export function zerlegeZeitplan(ausdruck) {
    const getrimmt = ausdruck.trim();
    const teile = getrimmt === '' ? [] : getrimmt.split(/\s+/);
    if (teile.length !== 5) {
        return {
            befund: {
                feld: 'ausdruck',
                satz: `Ein Zeitplan hat genau fünf Felder (Minute Stunde Tag Monat Wochentag), gefunden ${String(teile.length)}.`,
            },
        };
    }
    const gelesen = [];
    for (const [i, feld] of ZEITPLAN_FELDER.entries()) {
        const ergebnis = liesFeld(feld, teile[i] ?? '');
        if (typeof ergebnis === 'string') {
            return { befund: { feld, satz: `${FELDNAME[feld]}: ${ergebnis}.` } };
        }
        gelesen.push(ergebnis);
    }
    const [minuten = [], stunden = [], tage = [], monate = [], wochentage = []] = gelesen;
    return {
        zeitplan: {
            minuten,
            stunden,
            tage,
            monate,
            wochentage,
            tagEingeschraenkt: !(teile[2] ?? '').startsWith('*'),
            wochentagEingeschraenkt: !(teile[4] ?? '').startsWith('*'),
        },
    };
}
/**
 * Prüft einen Zeitplanausdruck vollständig: Form, Wertebereiche, Mindesttakt von
 * fünf Minuten und ob der Ausdruck überhaupt je eintritt (`0 0 31 2 *` tut es nie).
 *
 * @returns `null`, wenn der Ausdruck gültig ist, sonst der erste Befund.
 */
export function pruefeZeitplan(ausdruck) {
    const gelesen = zerlegeZeitplan(ausdruck);
    if ('befund' in gelesen)
        return gelesen.befund;
    const z = gelesen.zeitplan;
    if (kleinsterAbstand(z.minuten, z.stunden) < ZEITPLAN_MINDESTTAKT_MINUTEN) {
        return {
            feld: 'minute',
            satz: `Minute: Ein Workflow darf höchstens alle ${String(ZEITPLAN_MINDESTTAKT_MINUTEN)} Minuten laufen; „*“ oder ein kürzerer Abstand ist nicht erlaubt.`,
        };
    }
    const tagMoeglich = z.monate.some((m) => z.tage.some((t) => t <= (MONATSLAENGE[m - 1] ?? 0)));
    if (!tagMoeglich && !(z.tagEingeschraenkt && z.wochentagEingeschraenkt)) {
        return {
            feld: 'tag',
            satz: 'Tag: Diesen Tag gibt es in keinem der gewählten Monate; der Zeitplan liefe nie.',
        };
    }
    return null;
}
//# sourceMappingURL=workflows-zeitplan.js.map